import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const UPDATED_NOTICE_MS = 6000;

interface PreviewVersionFrameProps {
  /** Domino is reworking the app: the current version stays on screen. */
  revising: boolean;
  /** Changes whenever the shown version changes (draft signature). */
  versionKey: string | null;
  /** Number of the latest recorded version, when known. */
  versionNumber: number | null;
  children: ReactNode;
}

// Keeps the previous app visible while Domino reworks it, then marks the swap
// to the new version with a short notice and a soft reveal.
export const PreviewVersionFrame = ({
  revising,
  versionKey,
  versionNumber,
  children,
}: PreviewVersionFrameProps) => {
  const previousKeyRef = useRef(versionKey);
  const contentRef = useRef<HTMLDivElement>(null);
  const [swapCount, setSwapCount] = useState(0);
  const [showUpdated, setShowUpdated] = useState(false);

  useEffect(() => {
    const previousKey = previousKeyRef.current;
    previousKeyRef.current = versionKey;
    if (!previousKey || !versionKey || previousKey === versionKey) return;
    setSwapCount((count) => count + 1);
    setShowUpdated(true);
    // Animate in place: remounting would reset the preview's own state.
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) {
      contentRef.current?.animate?.(
        [
          { opacity: 0.35, transform: 'translateY(6px)', filter: 'blur(3px)' },
          { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
        ],
        { duration: 700, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
      );
    }
    const timer = window.setTimeout(() => setShowUpdated(false), UPDATED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [versionKey]);

  const notice = revising ? (
    <div
      role="status"
      className="flex items-center gap-2.5 rounded-full border border-domino-cardBorder bg-domino-warm/95 px-4 py-2 text-xs text-domino-ink shadow-sm backdrop-blur"
    >
      <span className="h-2 w-2 rounded-full bg-domino-sun motion-safe:animate-softPulse" aria-hidden="true" />
      <span>
        Domino retravaille l’app. Cette version reste affichée jusqu’à la suivante.
      </span>
    </div>
  ) : showUpdated ? (
    <div
      role="status"
      className="flex items-center gap-2 rounded-full border border-domino-cardBorder bg-domino-warm/95 px-4 py-2 text-xs text-domino-ink shadow-sm backdrop-blur animate-fadeIn"
    >
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-domino-sun text-[10px] text-domino-aubergine" aria-hidden="true">
        ✓
      </span>
      <span>
        Nouvelle version{versionNumber ? ` · v${versionNumber}` : ''}
      </span>
    </div>
  ) : null;

  return (
    <div className="relative min-h-full">
      {notice ? (
        <div className="pointer-events-none sticky top-3 z-20 flex h-0 justify-center">
          <div className="pointer-events-auto">{notice}</div>
        </div>
      ) : null}

      {showUpdated ? (
        <div className="pointer-events-none sticky top-0 z-10 h-0.5 overflow-hidden" aria-hidden="true">
          <div
            key={swapCount}
            className="h-full w-full bg-gradient-to-r from-transparent via-domino-sun to-transparent motion-safe:animate-versionSweep"
          />
        </div>
      ) : null}

      <div
        ref={contentRef}
        className={cn(
          'transition-[opacity,filter] duration-500',
          revising && 'opacity-60 saturate-50'
        )}
      >
        {children}
      </div>
    </div>
  );
};
